/**
 * ConstellationPage - Data-driven constellation explorer (DRR-085, DRR-090)
 *
 * The primary system comprehension tool. Any primitive can be the star (center)
 * of a constellation. The constellation selector determines the center entity;
 * the lens determines visibility per role.
 *
 * Accessible to ALL authenticated users — lens filtering handles restrictions.
 * Role-neutral: works for staff, volunteers, family members, external parties.
 * Backend controls what each user sees via lens + team scoping.
 *
 * Three data layers:
 * 1. Constellation template — center entity, rings, modes, scope bindings
 * 2. Lens template — role filtering, safety level, onboarding checklist
 * 3. Filtered constellation — template + lens applied = highlighted/de-emphasized
 *
 * Definition mode: all possible connections for entity TYPE (uppercase symbols, config source)
 * Instance mode: actual connections for specific RECORD (lowercase symbols, live source)
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Button,
  Collapse,
  Container,
  Divider,
  Group,
  Modal,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Stepper,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconArrowsMaximize,
  IconChecklist,
  IconChevronDown,
  IconChevronUp,
  IconCircleDot,
  IconEye,
  IconEyeOff,
  IconFilter,
  IconInfoCircle,
  IconLock,
  IconPencil,
  IconShieldCheck,
  IconStars,
  IconTarget,
  IconUsers,
  IconGraph,
} from '@tabler/icons-react';

import {
  useListConstellationTemplatesQuery,
  useGetConstellationDetailQuery,
} from '@/generated/graphql';
import { useLensTemplates } from '@/hooks/useLensTemplates';

import {
  useWorkflowConstellation,
  useLiveConstellation,
  usePrimitiveRelationshipExplorer,
  UniversalConstellationGraph,
  ConstellationView,
} from '@ferp/workflow-forms';
import type {
  WorkflowConstellationData,
  WorkflowConstellationNode,
  WorkflowConstellationEdge,
  UniversalConstellationData,
  ConstellationLayoutMode,
  ConstellationDefinitionNode,
} from '@ferp/workflow-forms';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { useResolvedTerminology } from '@/hooks/useResolvedTerminology';
import { ActorSearchSelect } from '@/pages/team/components/ActorSearchSelect';
import { useOnboardingReadiness } from '@/hooks/useOnboardingReadiness';
import { OnboardingReadinessRing } from '@/components/shared/OnboardingReadinessRing';
import { GoLiveReadinessBadge } from '@/components/shared/GoLiveReadinessBadge';

// Lens templates: use canonical useLensTemplates hook

// ============================================================================
// Types for query results
// ============================================================================

interface ConstellationTemplateItem {
  constellationId: string;
  name: string;
  description?: string | null;
  appliesTo: Array<{
    scope: string;
    industries: string[];
    centerLabel: string;
  }>;
}

interface ConstellationDetail {
  constellationId: string;
  version: string;
  name: string;
  description?: string | null;
  center: {
    primitiveType: string;
    actorType?: string | null;
    labelTemplate: string;
    description?: string | null;
  };
  modes?: {
    definition: { description: string; centerSource: string; ringSource: string; symbolCase: string };
    instance: { description: string; centerSource: string; ringSource: string; symbolCase: string };
  } | null;
  rings: Array<{
    ringId: string;
    ringNumber: number;
    label: string;
    description?: string | null;
    edgeTypes: string[];
    color?: string | null;
    shape?: string | null;
    definitionShows?: string | null;
    instanceShows?: string | null;
    instanceStatusFields: string[];
    provisioning?: string | null;
    onboardingAction?: string | null;
    definitionNodes?: Array<{
      taskGroupRef: string;
      workflowRef: string;
      department?: string | null;
      ownerRole?: string | null;
      label?: string | null;
      description?: string | null;
    }>;
  }>;
  appliesTo: Array<{
    scope: string;
    industries: string[];
    centerLabel: string;
    subjectType?: string | null;
    ringLabels?: Array<{ ringId: string; label: string }> | null;
  }>;
}

interface LensTemplateItem {
  lensId: string;
  name: string;
  description?: string | null;
  phase: string;
  safetyLevel?: string | null;
  verticals: string[];
  appliesToConstellation?: string | null;
  ruleCount: number;
}

// ============================================================================
// Constants
// ============================================================================

const RING_COLORS: Record<number, string> = {
  1: 'blue',
  2: 'violet',
  3: 'teal',
  4: 'orange',
  5: 'pink',
};

const SAFETY_COLORS: Record<string, string> = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'gray',
};

/** 25 primitive types grouped by lifecycle phase for the Explorer mode selector. */
const PRIMITIVE_TYPE_OPTIONS = [
  { group: 'Design', items: [
    { value: 'workflow', label: 'Workflow' },
    { value: 'template', label: 'Template' },
    { value: 'dependency', label: 'Dependency' },
    { value: 'policy', label: 'Policy' },
    { value: 'role', label: 'Role' },
  ]},
  { group: 'Configure', items: [
    { value: 'team', label: 'Team' },
    { value: 'actor', label: 'Actor' },
    { value: 'validation', label: 'Validation' },
    { value: 'contract', label: 'Contract' },
    { value: 'constraint', label: 'Constraint' },
  ]},
  { group: 'Execute', items: [
    { value: 'instance', label: 'Instance' },
    { value: 'task', label: 'Task' },
    { value: 'approval', label: 'Approval' },
    { value: 'document', label: 'Document' },
    { value: 'queue', label: 'Queue' },
  ]},
  { group: 'Monitor', items: [
    { value: 'event', label: 'Event' },
    { value: 'notification', label: 'Notification' },
    { value: 'trace', label: 'Trace' },
    { value: 'checkpoint', label: 'Checkpoint' },
    { value: 'transaction', label: 'Transaction' },
  ]},
  { group: 'Optimize', items: [
    { value: 'audit', label: 'Audit' },
    { value: 'analytics', label: 'Analytics' },
    { value: 'schedule', label: 'Schedule' },
    { value: 'resource', label: 'Resource' },
    { value: 'integration', label: 'Integration' },
  ]},
];

// ============================================================================
// Adapter: WorkflowConstellationData → UniversalConstellationData
// Maps 4-ring workflow structure to 3-tier universal format for visualization.
// ============================================================================

function toUniversalConstellation(
  wc: WorkflowConstellationData,
): UniversalConstellationData {
  const center = {
    nodeId: wc.center.nodeId,
    name: wc.center.name,
    role: 'center' as const,
  };

  let orgCount = 0;
  let regCount = 0;

  // Build a ring → access level mapping from hideFields/showFields
  const ringAccessMap: Record<string, { hidden: number; total: number }> = {};
  for (const ring of wc.rings) {
    const hidden = ring.hideFields.length;
    const total = hidden + ring.showFields.length;
    ringAccessMap[ring.ringId] = { hidden, total };
  }

  const nodes = wc.nodes.map((n: WorkflowConstellationNode) => {
    let role: 'center' | 'organization' | 'regulatory' | 'peer';
    if (n.role === 'center') {
      role = 'center';
    } else if (n.ringNumber <= 2) {
      role = 'organization';
      orgCount += 1;
    } else {
      role = 'regulatory';
      regCount += 1;
    }

    // Access overlay: annotate ring label nodes with access level
    const ringAccess = n.ringId ? ringAccessMap[n.ringId] : undefined;
    const accessLevel = ringAccess && ringAccess.total > 0
      ? (ringAccess.hidden === 0 ? 'full' : ringAccess.hidden === ringAccess.total ? 'none' : 'partial')
      : undefined;

    return {
      nodeId: n.nodeId,
      name: n.name,
      role,
      metadata: {
        ringId: n.ringId ?? '',
        ringNumber: String(n.ringNumber),
        highlighted: String(n.highlighted ?? false),
        deEmphasized: String(n.deEmphasized ?? false),
        ...(accessLevel ? {
          accessLevel,
          hiddenFieldCount: String(ringAccess?.hidden ?? 0),
          totalFieldCount: String(ringAccess?.total ?? 0),
        } : {}),
      },
    };
  });

  const edges = wc.edges.map((e: WorkflowConstellationEdge) => ({
    sourceId: e.sourceId,
    targetId: e.targetId,
    edgeType: e.edgeType,
    label: e.label,
  }));

  return {
    center,
    nodes,
    edges,
    organizationCount: orgCount,
    regulatoryCount: regCount,
    uniqueRegulatoryCount: regCount,
    sharedRegulatoryCount: 0,
  };
}

// ============================================================================
// Component
// ============================================================================

export function ConstellationPage() {
  const navigate = useNavigate();
  const [constellationId, setConstellationId] = useState<string | null>(null);
  const [lensId, setLensId] = useState<string | null>(null);
  const [checklistOpen, { toggle: toggleChecklist }] = useDisclosure(true);
  const [graphExpanded, { open: openGraphModal, close: closeGraphModal }] = useDisclosure(false);
  const [readinessExpanded, { toggle: toggleReadiness }] = useDisclosure(true);
  const [layoutMode, setLayoutMode] = useState<ConstellationLayoutMode>('concentric');
  const { user } = useAuth();
  const { getLabel } = useResolvedTerminology();

  // ── Onboarding Readiness (DRR-184) ────────────────────────────────────
  const {
    dimensions: readinessDimensions,
    overallStatus,
    blockingRings,
    overallPercent,
    loading: readinessLoading,
  } = useOnboardingReadiness();

  // ── Entity/actor selector for Instance + User Assignments modes ───────
  // Default to current user; admin/architect can pick any actor.
  const [selectedActorId, setSelectedActorId] = useState<string | null>(null);
  const effectiveActorId = selectedActorId || user?.id || '';
  const effectiveActorName = selectedActorId
    ? `Actor ${selectedActorId}`
    : (user?.displayName || 'Me');

  // ── Live constellation: Instance + User Assignments modes ─────────────
  const {
    constellation: liveConstellation,
    isLoading: liveLoading,
    error: liveError,
  } = useLiveConstellation({
    centerId: effectiveActorId,
    centerName: effectiveActorName,
    skip: false,
  });

  // ── Query 1: List constellation templates (selector) ──────────────────
  const { data: listData, loading: listLoading } = useListConstellationTemplatesQuery({
    fetchPolicy: 'cache-and-network',
  });
  const templates = listData?.constellationTemplates ?? [];

  // ── Query 2: Full constellation detail (center, modes, ring descriptions) ──
  const { data: detailData, loading: detailLoading } = useGetConstellationDetailQuery({
    variables: { constellationId: constellationId ?? '' },
    fetchPolicy: 'cache-and-network',
    skip: !constellationId,
  });
  const detail = detailData?.constellationTemplate ?? null;

  // DRR-126: Extract Ring 3 definition nodes for ConstellationView
  const ring3DefinitionNodes = useMemo(() => {
    if (!detail?.rings) return [];
    const ring3 = detail.rings.find((r) => r.ringNumber === 3);
    return ((ring3 as unknown as Record<string, unknown>)?.definitionNodes ?? []) as ConstellationDefinitionNode[];
  }, [detail]);

  // DRR-126: Build definition-mode data skeleton from template rings
  // In definition mode there's no live data — build center + ring shells from template
  const definitionModeData = useMemo(() => {
    if (!detail) return null;
    const center = {
      nodeId: detail.constellationId,
      name: detail.center.labelTemplate,
      role: 'center' as const,
      ring: 0,
    };
    // Build placeholder nodes for rings 1, 2, 4 from template ring descriptions
    const nodes: { nodeId: string; name: string; role: string; ring: number }[] = [center];
    const edges: { sourceId: string; targetId: string; edgeType: string; label?: string }[] = [];
    for (const ring of detail.rings) {
      if (ring.ringNumber === 3) continue; // Ring 3 comes from definitionNodes
      const nodeId = `ring-${ring.ringId}`;
      nodes.push({
        nodeId,
        name: (ring as Record<string, unknown>).definitionShows as string ?? ring.label,
        role: ring.ringId,
        ring: ring.ringNumber,
      });
      edges.push({
        sourceId: center.nodeId,
        targetId: nodeId,
        edgeType: ring.edgeTypes[0] ?? 'CONTAINS',
      });
    }
    return { center, nodes, edges };
  }, [detail]);

  // DRR-126: Ring metadata from backend template for data-driven ring labels/colors
  const ringMeta = useMemo(() => {
    if (!detail?.rings) return [];
    return detail.rings.map((r) => ({
      ringNumber: r.ringNumber,
      label: r.label,
      color: r.color ?? undefined,
    }));
  }, [detail]);

  // DRR-126: Navigate to workflow fragment inspector when clicking a definition node
  const handleWorkflowClick = useCallback((workflowRef: string) => {
    navigate(`/ferp-core/workflows/studio?tab=inspector&workflowId=${workflowRef}&workflowName=${encodeURIComponent(workflowRef)}`);
  }, [navigate]);

  // ── Query 3: Lens templates (expanded with safety + rule count) ───────
  // Don't filter by constellation — most lenses aren't tagged with one.
  // Show all lenses for the vertical so the user can apply any perspective.
  const { templates: lensTemplates, loading: lensesLoading } = useLensTemplates();

  // ── Query 4: Filtered constellation (template + lens applied) ─────────
  const {
    constellation,
    isLoading,
    error,
    mode,
    setMode,
    checklist,
  } = useWorkflowConstellation({
    constellationId: constellationId ?? '',
    lensId: lensId ?? '',
    initialMode: 'definition',
  });

  // ── Selectors ─────────────────────────────────────────────────────────
  const constellationOptions = useMemo(
    () => templates.map((c) => ({ value: c.constellationId, label: c.name })),
    [templates],
  );

  const lensOptions = useMemo(() => {
    // Mantine v8 grouped Select requires { group, items } format (not flat group prop)
    const groups = new Map<string, Array<{ value: string; label: string }>>();
    for (const l of lensTemplates) {
      if (!l.lensId || !l.name) continue;
      const phase = l.phase || 'Other';
      if (!groups.has(phase)) groups.set(phase, []);
      groups.get(phase)!.push({ value: l.lensId, label: l.name });
    }
    return Array.from(groups.entries()).map(([group, items]) => ({ group, items }));
  }, [lensTemplates]);

  // Auto-select first constellation/lens when data loads
  const firstConstellationId = templates[0]?.constellationId;
  const firstLensId = lensTemplates[0]?.lensId;
  if (!constellationId && firstConstellationId) {
    setConstellationId(firstConstellationId);
  }
  if (!lensId && firstLensId) {
    setLensId(firstLensId);
  }

  // Active lens metadata
  const activeLens = useMemo(
    () => lensTemplates.find((l) => l.lensId === lensId),
    [lensTemplates, lensId],
  );

  // ── Explorer mode state ──────────────────────────────────────────────
  const [explorerPrimitiveType, setExplorerPrimitiveType] = useState<string | null>('workflow');
  const [explorerPrimitiveId, setExplorerPrimitiveId] = useState<string>('');

  const {
    constellation: explorerConstellation,
    relatedCounts: explorerRelatedCounts,
    loading: explorerLoading,
    error: explorerError,
  } = usePrimitiveRelationshipExplorer({
    primitiveType: explorerPrimitiveType ?? '',
    primitiveId: explorerPrimitiveId,
    maxPerType: 20,
    skip: !explorerPrimitiveType || !explorerPrimitiveId,
  });

  // viewMode tracks the 5-way switch: definition, instance, myAssignments, careTeam, explorer
  type ViewMode = 'definition' | 'instance' | 'myAssignments' | 'careTeam' | 'explorer';
  const [viewMode, setViewMode] = useState<ViewMode>('definition');
  const handleViewModeChange = useCallback(
    (value: string) => {
      const v = value as ViewMode;
      setViewMode(v);
      if (v !== 'myAssignments' && v !== 'careTeam' && v !== 'explorer') {
        setMode(v);
      }
    },
    [setMode],
  );

  // Adapter for UniversalConstellationGraph — switches data source by view mode
  // Instance + User Assignments both use the live scope graph (centered on selected actor)
  // Definition uses the template constellation (all possible connections for the type)
  // Care Team uses live constellation repackaged into care team ring structure
  const universalData = useMemo(() => {
    if (viewMode === 'explorer') return explorerConstellation;
    if (viewMode === 'instance' || viewMode === 'myAssignments' || viewMode === 'careTeam') return liveConstellation;
    if (!constellation) return null;
    return toUniversalConstellation(constellation);
  }, [viewMode, constellation, liveConstellation, explorerConstellation]);

  // Care team data adapter — maps universal constellation data to care team rings
  const careTeamData = useMemo(() => {
    if (viewMode !== 'careTeam' || !liveConstellation) return null;
    const center = {
      nodeId: liveConstellation.center?.nodeId ?? 'unknown',
      name: liveConstellation.center?.name ?? getLabel('Patient'),
      role: 'center',
      ring: 0,
    };
    const nodes = [center];
    const edges: { sourceId: string; targetId: string; edgeType: string; label?: string }[] = [];

    // Map universal constellation nodes to care team rings
    // ConstellationRole: 'center' | 'organization' | 'regulatory' | 'peer'
    for (const node of liveConstellation.nodes ?? []) {
      if (node.nodeId === center.nodeId) continue;
      // Classify by ConstellationRole into care team rings
      const ring = node.role === 'organization' ? 2
        : node.role === 'regulatory' ? 4
        : node.role === 'peer' ? 1
        : 1; // default to clinical staff ring
      nodes.push({
        nodeId: node.nodeId,
        name: node.name,
        role: node.role as string,
        ring,
      });
    }

    for (const edge of liveConstellation.edges ?? []) {
      edges.push({
        sourceId: edge.sourceId,
        targetId: edge.targetId,
        edgeType: edge.edgeType ?? 'ASSIGNED_TO',
        label: edge.label,
      });
    }

    return { center, nodes, edges };
  }, [viewMode, liveConstellation]);

  // Active checklist step (first incomplete)
  const activeStep = useMemo(() => {
    if (!checklist.length) return 0;
    return Math.max(0, checklist.length - 1);
  }, [checklist]);

  // Mode description from template detail
  const modeInfo = (detail as Record<string, unknown>)?.modes as Record<string, unknown> | undefined;
  const activeModeInfo = modeInfo?.[mode] ?? null;

  return (
    <Container size="xl">
      <Stack gap="lg">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon variant="light" size="lg">
              <IconStars size={20} />
            </ThemeIcon>
            <div>
              <Title order={2}>Constellation Explorer</Title>
              <Text c="dimmed" size="sm">
                Understand structure, relationships, and behavior
              </Text>
            </div>
          </Group>
          <Badge variant="light" size="lg">
            {viewMode === 'definition' ? 'Template View' : viewMode === 'instance' ? 'Live View' : viewMode === 'careTeam' ? 'Care Team' : 'User Assignments'}
          </Badge>
        </Group>

        {/* ── Controls: Constellation + Lens + Mode ──────────────────── */}
        <Paper p="md" withBorder>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <Select
              label="Constellation"
              placeholder={listLoading ? 'Loading...' : 'Select a constellation...'}
              data={constellationOptions}
              value={constellationId}
              onChange={(val) => {
                setConstellationId(val);
                setLensId(null);
              }}
              searchable
              disabled={listLoading}
            />

            <Select
              label="Lens (perspective)"
              placeholder={lensesLoading ? 'Loading...' : 'Select a lens...'}
              data={lensOptions}
              value={lensId}
              onChange={setLensId}
              searchable
              disabled={lensesLoading}
            />

            <div>
              <Text size="sm" fw={500} mb={5}>
                Mode
              </Text>
              <SegmentedControl
                value={viewMode}
                onChange={handleViewModeChange}
                data={[
                  {
                    value: 'definition',
                    label: (
                      <Group gap={4}>
                        <IconEyeOff size={14} />
                        <Text size="sm">Definition</Text>
                      </Group>
                    ),
                  },
                  {
                    value: 'instance',
                    label: (
                      <Group gap={4}>
                        <IconEye size={14} />
                        <Text size="sm">Instance</Text>
                      </Group>
                    ),
                  },
                  {
                    value: 'myAssignments',
                    label: (
                      <Group gap={4}>
                        <IconTarget size={14} />
                        <Text size="sm">User Assignments</Text>
                      </Group>
                    ),
                  },
                  {
                    value: 'careTeam',
                    label: (
                      <Group gap={4}>
                        <IconUsers size={14} />
                        <Text size="sm">Care Team</Text>
                      </Group>
                    ),
                  },
                  {
                    value: 'explorer',
                    label: (
                      <Group gap={4}>
                        <IconGraph size={14} />
                        <Text size="sm">Explorer</Text>
                      </Group>
                    ),
                  },
                ]}
                fullWidth
              />
            </div>
          </SimpleGrid>
        </Paper>

        {/* ── Mode Context Banner ────────────────────────────────────── */}
        {viewMode === 'explorer' ? (
          <Paper p="sm" withBorder bg="violet.0">
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <ThemeIcon variant="light" color="violet" size="sm" mt={4}>
                <IconGraph size={14} />
              </ThemeIcon>
              <Stack gap="xs" style={{ flex: 1 }}>
                <Group gap="md" align="flex-end">
                  <Select
                    label="Primitive Type"
                    placeholder="Select type..."
                    data={PRIMITIVE_TYPE_OPTIONS}
                    value={explorerPrimitiveType}
                    onChange={setExplorerPrimitiveType}
                    searchable
                    style={{ flex: 1, maxWidth: 200 }}
                  />
                  <TextInput
                    label="Instance ID"
                    placeholder="e.g., wf-0-0-001"
                    value={explorerPrimitiveId}
                    onChange={(e) => setExplorerPrimitiveId(e.currentTarget.value)}
                    style={{ flex: 1, maxWidth: 300 }}
                  />
                </Group>
                <Text size="xs" c="dimmed">
                  Select any primitive type and enter an instance ID to explore its relationships.
                  Double-click a node to re-center the graph on it.
                </Text>
                {explorerRelatedCounts.length > 0 && (
                  <Group gap={4}>
                    {explorerRelatedCounts.map((rc) => (
                      <Badge key={rc.primitiveType} size="xs" variant="light" color="violet">
                        {rc.count} {rc.edgeLabel}
                      </Badge>
                    ))}
                  </Group>
                )}
              </Stack>
            </Group>
          </Paper>
        ) : (viewMode === 'instance' || viewMode === 'myAssignments') ? (
          <Paper p="sm" withBorder bg={viewMode === 'instance' ? 'green.0' : 'teal.0'}>
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <ThemeIcon
                variant="light"
                color={viewMode === 'instance' ? 'green' : 'teal'}
                size="sm"
                mt={4}
              >
                {viewMode === 'instance' ? <IconEye size={14} /> : <IconTarget size={14} />}
              </ThemeIcon>
              <Stack gap="xs" style={{ flex: 1 }}>
                <Group gap="md" align="flex-end">
                  <div style={{ flex: 1, maxWidth: 300 }}>
                    <ActorSearchSelect
                      label={viewMode === 'instance'
                        ? `Select ${detail?.center.primitiveType ?? 'entity'} owner`
                        : 'View assignments for'
                      }
                      value={selectedActorId}
                      onChange={setSelectedActorId}
                      placeholder={user?.displayName ?? 'Select user...'}
                      clearable
                    />
                  </div>
                  {!selectedActorId && (
                    <Badge size="sm" variant="light" color={viewMode === 'instance' ? 'green' : 'teal'}>
                      Showing: {user?.displayName ?? 'Me'} (you)
                    </Badge>
                  )}
                </Group>
                <Text size="xs" c="dimmed">
                  {viewMode === 'instance'
                    ? `Live connections for ${effectiveActorName}. Select a user to see their actual ${detail?.center.primitiveType ?? 'entity'} relationships.`
                    : `Live scope edges for ${effectiveActorName}. Center = selected user. Ring 1 = assigned entities. Ring 2 = containing structures.`
                  }
                </Text>
                <Group gap="xs">
                  <Badge size="xs" variant="light" color="gray">Source: live scope graph</Badge>
                  <Badge size="xs" variant="light" color="gray">DRR-102: fail-closed</Badge>
                </Group>
              </Stack>
            </Group>
          </Paper>
        ) : activeModeInfo ? (
          <Paper p="sm" withBorder bg="blue.0">
            <Group gap="xs" wrap="nowrap">
              <ThemeIcon variant="light" color="blue" size="sm">
                <IconInfoCircle size={14} />
              </ThemeIcon>
              <div>
                <Text size="sm" fw={600}>Definition Mode</Text>
                <Text size="xs" c="dimmed">
                  {(activeModeInfo as Record<string, unknown>).description as string}
                </Text>
                <Group gap="xs" mt={2}>
                  <Badge size="xs" variant="light" color="gray">
                    Source: {(activeModeInfo as Record<string, unknown>).centerSource as string}
                  </Badge>
                  <Badge size="xs" variant="light" color="gray">
                    Symbols: {(activeModeInfo as Record<string, unknown>).symbolCase as string}
                  </Badge>
                </Group>
              </div>
            </Group>
          </Paper>
        ) : null}

        {/* ── Error ──────────────────────────────────────────────────── */}
        {(error || liveError || explorerError) && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            Failed to load constellation: {(error ?? liveError ?? explorerError)?.message}
          </Alert>
        )}

        {/* ── Main Content: Graph (3 cols) + Sidebar (1 col) ─────────── */}
        <SimpleGrid cols={{ base: 1, lg: 4 }} spacing="md">
          {/* Graph */}
          <div style={{ gridColumn: 'span 3' }}>
            {(viewMode === 'explorer' ? explorerLoading : (viewMode === 'instance' || viewMode === 'myAssignments') ? liveLoading : isLoading || detailLoading) ? (
              <Skeleton height={500} radius="md" />
            ) : !universalData ? (
              <Paper p="xl" withBorder ta="center" mih={400}>
                <Stack gap="md" align="center" justify="center" mih={300}>
                  <IconStars size={48} color="gray" />
                  <Text size="lg" fw={500}>
                    Select a Constellation + Lens
                  </Text>
                  <Text c="dimmed" size="sm" maw={400}>
                    Choose a constellation type and a lens to visualize relationships.
                    Definition mode shows all possible connections; Instance mode shows
                    actual records for a specific entity.
                  </Text>
                </Stack>
              </Paper>
            ) : (
              <>
                {/* Layout toggle + expand button */}
                <Group justify="space-between" mb="xs">
                  <SegmentedControl
                    size="xs"
                    value={layoutMode}
                    onChange={(v) => setLayoutMode(v as ConstellationLayoutMode)}
                    data={[
                      { label: 'Rings', value: 'concentric' },
                      { label: 'Mind Map', value: 'mindmap' },
                    ]}
                  />
                  <Tooltip label="Expand to fullscreen">
                    <ActionIcon variant="light" color="gray" onClick={openGraphModal}>
                      <IconArrowsMaximize size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Paper withBorder style={{ overflow: 'hidden' }}>
                  {viewMode === 'careTeam' && careTeamData ? (
                    <ConstellationView
                      center={careTeamData.center}
                      nodes={careTeamData.nodes}
                      edges={careTeamData.edges}
                      height={500}
                      mode="instance"
                    />
                  ) : viewMode === 'definition' && ring3DefinitionNodes.length > 0 && definitionModeData ? (
                    <ConstellationView
                      center={definitionModeData.center}
                      nodes={definitionModeData.nodes}
                      edges={definitionModeData.edges}
                      height={500}
                      mode="definition"
                      definitionNodes={ring3DefinitionNodes}
                      onWorkflowClick={handleWorkflowClick}
                      ringMeta={ringMeta}
                    />
                  ) : (
                    <UniversalConstellationGraph
                      constellation={universalData}
                      height={500}
                      layoutMode={viewMode === 'explorer' ? 'mindmap' : layoutMode}
                      onRecenter={viewMode === 'explorer' ? (nodeId) => {
                        // Parse "primitiveType:instanceId" format from node ID
                        const colonIdx = nodeId.indexOf(':');
                        if (colonIdx > 0) {
                          setExplorerPrimitiveType(nodeId.substring(0, colonIdx));
                          setExplorerPrimitiveId(nodeId.substring(colonIdx + 1));
                        }
                      } : undefined}
                    />
                  )}
                </Paper>
              </>
            )}
          </div>

          {/* Sidebar */}
          <Stack gap="md">
            {/* Explorer Sidebar */}
            {viewMode === 'explorer' && explorerConstellation && (
              <Paper p="sm" withBorder>
                <Group gap="xs" mb="xs">
                  <IconGraph size={14} />
                  <Text size="sm" fw={600}>Relationship Explorer</Text>
                </Group>
                <Stack gap={4}>
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">Center</Text>
                    <Badge size="xs" variant="light" color="violet">
                      {explorerConstellation.center.name}
                    </Badge>
                  </Group>
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">Connected</Text>
                    <Text size="xs" fw={500}>
                      {explorerConstellation.nodes.length - 1} nodes
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">Edges</Text>
                    <Text size="xs" fw={500}>
                      {explorerConstellation.edges.length}
                    </Text>
                  </Group>
                  {explorerRelatedCounts.length > 0 && (
                    <>
                      <Divider my={4} />
                      <Text size="xs" c="dimmed" fw={500}>By Type</Text>
                      {explorerRelatedCounts.map((rc) => (
                        <Group key={rc.primitiveType} justify="space-between">
                          <Text size="xs">{rc.edgeLabel}</Text>
                          <Badge size="xs" variant="light">{rc.count}</Badge>
                        </Group>
                      ))}
                    </>
                  )}
                </Stack>
              </Paper>
            )}

            {/* Onboarding Readiness (DRR-184) */}
            <Paper p="sm" withBorder>
              <Group gap="xs" justify="space-between" mb="xs">
                <Group gap="xs">
                  <IconChecklist size={14} />
                  <Text size="sm" fw={600}>Go-Live Readiness</Text>
                </Group>
                <GoLiveReadinessBadge
                  status={overallStatus}
                  blockingRings={blockingRings}
                  percent={overallPercent}
                  loading={readinessLoading}
                  size="sm"
                />
              </Group>
              {/* Compact ring display row */}
              <Group gap="xs" justify="center" mb="xs">
                {readinessDimensions.map((dim) => (
                  <OnboardingReadinessRing
                    key={dim.id}
                    dimension={dim}
                    compact
                    size={40}
                    thickness={4}
                    expandable={false}
                  />
                ))}
              </Group>
              <Button
                size="compact-xs"
                variant="subtle"
                onClick={toggleReadiness}
                fullWidth
                rightSection={
                  readinessExpanded
                    ? <IconChevronUp size={12} />
                    : <IconChevronDown size={12} />
                }
              >
                {readinessExpanded ? 'Hide' : 'Show'} Details
              </Button>
              <Collapse in={readinessExpanded}>
                <Stack gap={4} mt="sm">
                  {readinessDimensions.map((dim) => (
                    <Group key={dim.id} justify="space-between" wrap="nowrap">
                      <Group gap={4} wrap="nowrap">
                        <Badge size="xs" variant="filled" color={dim.color} w={40}>
                          {dim.id}
                        </Badge>
                        <Text size="xs" c="dimmed" truncate style={{ maxWidth: 80 }}>
                          {dim.label}
                        </Text>
                      </Group>
                      <Group gap={4} wrap="nowrap">
                        <Text size="xs" fw={500}>{dim.completed}/{dim.total}</Text>
                        <Badge
                          size="xs"
                          variant="light"
                          color={dim.readiness === 'GREEN' ? 'green' : dim.readiness === 'YELLOW' ? 'yellow' : 'red'}
                        >
                          {dim.percentComplete}%
                        </Badge>
                      </Group>
                    </Group>
                  ))}
                </Stack>
              </Collapse>
            </Paper>

            {/* Center Entity */}
            {detail && viewMode !== 'explorer' && (
              <Paper p="sm" withBorder>
                <Group gap="xs" mb="xs">
                  <IconTarget size={14} />
                  <Text size="sm" fw={600}>Center Entity</Text>
                </Group>
                <Stack gap={4}>
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">Type</Text>
                    <Badge size="xs" variant="light">{detail.center.primitiveType}</Badge>
                  </Group>
                  {detail.center.actorType && (
                    <Group justify="space-between">
                      <Text size="xs" c="dimmed">Subtype</Text>
                      <Text size="xs" fw={500}>{detail.center.actorType}</Text>
                    </Group>
                  )}
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">Label</Text>
                    <Text size="xs" fw={500} style={{ fontFamily: 'monospace' }}>
                      {detail.center.labelTemplate}
                    </Text>
                  </Group>
                  {detail.center.description && (
                    <Text size="xs" c="dimmed" mt={2}>
                      {detail.center.description}
                    </Text>
                  )}
                  <Divider my={4} />
                  <Text size="xs" c="dimmed" fw={500}>Scope Bindings</Text>
                  {detail.appliesTo.map((binding, idx) => (
                    <Group key={`${binding.scope}-${idx}`} gap={4} wrap="wrap">
                      <Badge size="xs" variant="filled" color="grape">
                        {binding.scope}
                      </Badge>
                      <Text size="xs">{binding.centerLabel}</Text>
                      {binding.industries.map((ind) => (
                        <Badge key={ind} size="xs" variant="light" color="gray">
                          {ind.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </Group>
                  ))}
                  <Group justify="space-between" mt={4}>
                    <Text size="xs" c="dimmed">Version</Text>
                    <Badge size="xs" variant="light" color="gray">{detail.version}</Badge>
                  </Group>
                </Stack>
              </Paper>
            )}

            {/* Active Lens */}
            {activeLens && (
              <Paper p="sm" withBorder>
                <Group gap="xs" mb="xs">
                  <IconFilter size={14} />
                  <Text size="sm" fw={600}>Active Lens</Text>
                </Group>
                <Stack gap={4}>
                  <Text size="xs" fw={500}>{activeLens.name}</Text>
                  {activeLens.description && (
                    <Text size="xs" c="dimmed">{activeLens.description}</Text>
                  )}
                  <Group gap="xs" mt={2}>
                    <Badge size="xs" variant="light">
                      Phase: {activeLens.phase}
                    </Badge>
                    {activeLens.safetyLevel && (
                      <Badge
                        size="xs"
                        variant="filled"
                        color={SAFETY_COLORS[activeLens.safetyLevel] ?? 'gray'}
                      >
                        {activeLens.safetyLevel}
                      </Badge>
                    )}
                    <Badge size="xs" variant="light" color="blue">
                      {activeLens.ruleCount} rules
                    </Badge>
                  </Group>
                  {activeLens.verticals.length > 0 && (
                    <Group gap={4} mt={2}>
                      {activeLens.verticals.map((v) => (
                        <Badge key={v} size="xs" variant="dot" color="gray">
                          {v.replace(/-/g, ' ')}
                        </Badge>
                      ))}
                    </Group>
                  )}
                </Stack>
              </Paper>
            )}

            {/* Ring Legend */}
            <Paper p="sm" withBorder>
              <Text size="sm" fw={600} mb="xs">
                Rings
              </Text>
              {constellation ? (
                <Stack gap={4}>
                  {constellation.rings.map((ring) => (
                    <Group key={ring.ringId} gap="xs" wrap="nowrap">
                      <Badge
                        size="xs"
                        color={RING_COLORS[ring.ringNumber] ?? 'gray'}
                        variant="filled"
                        w={20}
                        p={0}
                        style={{ textAlign: 'center' }}
                      >
                        {ring.ringNumber}
                      </Badge>
                      <Text size="xs" truncate style={{ flex: 1 }}>
                        {ring.label}
                      </Text>
                      {ring.highlighted.length > 0 && (
                        <Tooltip label={`${ring.highlighted.length} highlighted by lens`}>
                          <Badge size="xs" variant="light" color="yellow">
                            {ring.highlighted.length}
                          </Badge>
                        </Tooltip>
                      )}
                      {ring.deEmphasized.length > 0 && (
                        <Tooltip label={`${ring.deEmphasized.length} de-emphasized`}>
                          <Badge size="xs" variant="light" color="gray">
                            {ring.deEmphasized.length}
                          </Badge>
                        </Tooltip>
                      )}
                    </Group>
                  ))}
                </Stack>
              ) : (
                <Text size="xs" c="dimmed">No constellation loaded</Text>
              )}
            </Paper>

            {/* Stats */}
            {constellation && (
              <Paper p="sm" withBorder>
                <Text size="sm" fw={600} mb="xs">Stats</Text>
                <Stack gap={4}>
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">Rings</Text>
                    <Text size="xs" fw={500}>{constellation.ringCount}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">Highlighted</Text>
                    <Text size="xs" fw={500}>{constellation.highlightedCount}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">Lens</Text>
                    <Text size="xs" fw={500}>{constellation.lensName}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">Mode</Text>
                    <Badge size="xs" variant="light">
                      {constellation.mode}
                    </Badge>
                  </Group>
                </Stack>
              </Paper>
            )}
          </Stack>
        </SimpleGrid>

        {/* ── Ring Details (Accordion) ───────────────────────────────── */}
        {constellation && detail && (
          <>
            <Divider
              label={
                <Group gap="xs">
                  <IconCircleDot size={14} />
                  <Text size="sm" fw={600}>Ring Details</Text>
                </Group>
              }
              labelPosition="left"
            />
            <Accordion variant="separated" multiple>
              {constellation.rings.map((filteredRing) => {
                const templateRing = detail.rings.find(
                  (r) => r.ringId === filteredRing.ringId,
                );
                const totalItems = filteredRing.highlighted.length + filteredRing.deEmphasized.length;
                const isDraft = filteredRing.provisioning === 'draft';

                return (
                  <Accordion.Item key={filteredRing.ringId} value={filteredRing.ringId}>
                    <Accordion.Control>
                      <Group gap="xs" wrap="nowrap">
                        <Badge
                          size="sm"
                          color={RING_COLORS[filteredRing.ringNumber] ?? 'gray'}
                          variant="filled"
                          w={24}
                          p={0}
                          style={{ textAlign: 'center' }}
                        >
                          {filteredRing.ringNumber}
                        </Badge>
                        <Text size="sm" fw={600}>{filteredRing.label}</Text>
                        <Badge size="xs" variant="light" color="blue">
                          {totalItems} items
                        </Badge>
                        {filteredRing.highlighted.length > 0 && (
                          <Badge size="xs" variant="light" color="yellow">
                            {filteredRing.highlighted.length} highlighted
                          </Badge>
                        )}
                        {filteredRing.deEmphasized.length > 0 && (
                          <Badge size="xs" variant="light" color="gray">
                            {filteredRing.deEmphasized.length} dimmed
                          </Badge>
                        )}
                        <Tooltip label={isDraft ? 'Customizable' : 'Accept-only'}>
                          <ThemeIcon
                            variant="light"
                            color={isDraft ? 'blue' : 'orange'}
                            size="xs"
                          >
                            {isDraft ? <IconPencil size={10} /> : <IconLock size={10} />}
                          </ThemeIcon>
                        </Tooltip>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="sm">
                        {/* Template-level description */}
                        {templateRing?.description && (
                          <Text size="xs" c="dimmed">{templateRing.description}</Text>
                        )}

                        {/* Edge types */}
                        {templateRing && templateRing.edgeTypes.length > 0 && (
                          <div>
                            <Text size="xs" fw={500} mb={2}>Edge Types</Text>
                            <Group gap={4}>
                              {templateRing.edgeTypes.map((et) => (
                                <Badge key={et} size="xs" variant="outline" color="gray">
                                  {et}
                                </Badge>
                              ))}
                            </Group>
                          </div>
                        )}

                        {/* What each mode shows */}
                        <SimpleGrid cols={2} spacing="xs">
                          <Paper p="xs" bg="blue.0" radius="sm">
                            <Text size="xs" fw={500} c="blue.7">Definition shows</Text>
                            <Text size="xs">
                              {(templateRing as Record<string, unknown>)?.definitionShows as string ?? filteredRing.modeDescription ?? 'All possible entity types'}
                            </Text>
                          </Paper>
                          <Paper p="xs" bg="green.0" radius="sm">
                            <Text size="xs" fw={500} c="green.7">Instance shows</Text>
                            <Text size="xs">
                              {(templateRing as Record<string, unknown>)?.instanceShows as string ?? 'Actual records for this entity'}
                            </Text>
                          </Paper>
                        </SimpleGrid>

                        {/* Onboarding action */}
                        {filteredRing.onboardingAction && (
                          <Group gap="xs">
                            <IconChecklist size={12} />
                            <Text size="xs" fw={500}>Onboarding:</Text>
                            <Text size="xs">{filteredRing.onboardingAction}</Text>
                          </Group>
                        )}

                        {/* Highlighted items */}
                        {filteredRing.highlighted.length > 0 && (
                          <div>
                            <Text size="xs" fw={500} mb={2}>
                              Highlighted ({filteredRing.highlighted.length})
                            </Text>
                            <Group gap={4}>
                              {filteredRing.highlighted.map((id) => (
                                <Badge key={id} size="xs" variant="filled" color="yellow" c="dark">
                                  {id}
                                </Badge>
                              ))}
                            </Group>
                          </div>
                        )}

                        {/* De-emphasized items */}
                        {filteredRing.deEmphasized.length > 0 && (
                          <div>
                            <Text size="xs" fw={500} mb={2}>
                              De-emphasized ({filteredRing.deEmphasized.length})
                            </Text>
                            <Group gap={4}>
                              {filteredRing.deEmphasized.map((id) => (
                                <Badge key={id} size="xs" variant="light" color="gray">
                                  {id}
                                </Badge>
                              ))}
                            </Group>
                          </div>
                        )}

                        {/* Show/Hide fields from lens */}
                        {filteredRing.showFields.length > 0 && (
                          <div>
                            <Text size="xs" fw={500} mb={2}>Visible Fields</Text>
                            <Group gap={4}>
                              {filteredRing.showFields.map((f) => (
                                <Badge key={f} size="xs" variant="dot" color="green">{f}</Badge>
                              ))}
                            </Group>
                          </div>
                        )}
                        {filteredRing.hideFields.length > 0 && (
                          <div>
                            <Text size="xs" fw={500} mb={2}>Hidden Fields</Text>
                            <Group gap={4}>
                              {filteredRing.hideFields.map((f) => (
                                <Badge key={f} size="xs" variant="dot" color="red">{f}</Badge>
                              ))}
                            </Group>
                          </div>
                        )}

                        {/* Expand sections (regulatory) */}
                        {filteredRing.expandSections.length > 0 && (
                          <div>
                            <Text size="xs" fw={500} mb={2}>Expanded Sections</Text>
                            <Group gap={4}>
                              {filteredRing.expandSections.map((s) => (
                                <Badge key={s} size="xs" variant="outline" color="violet">
                                  {s}
                                </Badge>
                              ))}
                            </Group>
                          </div>
                        )}

                        {/* Instance status fields */}
                        {filteredRing.instanceStatusFields.length > 0 && (
                          <div>
                            <Text component="div" size="xs" fw={500} mb={2}>
                              <Group gap={4}>
                                <IconShieldCheck size={12} />
                                Instance Status Fields
                              </Group>
                            </Text>
                            <Group gap={4}>
                              {filteredRing.instanceStatusFields.map((sf) => (
                                <Badge
                                  key={sf}
                                  size="xs"
                                  variant="light"
                                  color={mode === 'instance' ? 'green' : 'gray'}
                                >
                                  {sf}
                                </Badge>
                              ))}
                            </Group>
                            {mode === 'definition' && (
                              <Text size="xs" c="dimmed" fs="italic" mt={2}>
                                Switch to Instance mode to see live values
                              </Text>
                            )}
                          </div>
                        )}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </>
        )}

        {/* ── Onboarding Checklist ───────────────────────────────────── */}
        {checklist.length > 0 && (
          <>
            <Divider
              label={
                <Group gap="xs">
                  <IconChecklist size={14} />
                  <Text size="sm" fw={600}>Onboarding Checklist</Text>
                  <Badge size="xs" variant="light">{checklist.length} steps</Badge>
                </Group>
              }
              labelPosition="left"
            />
            <Paper p="md" withBorder>
              <Group justify="flex-end" mb="xs">
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={toggleChecklist}
                  rightSection={
                    checklistOpen
                      ? <IconChevronUp size={12} />
                      : <IconChevronDown size={12} />
                  }
                >
                  {checklistOpen ? 'Collapse' : 'Expand'}
                </Button>
              </Group>
              <Collapse in={checklistOpen}>
                <Stepper
                  active={activeStep}
                  orientation="vertical"
                  size="sm"
                >
                  {checklist.map((step, i) => (
                    <Stepper.Step
                      key={i}
                      label={step.step}
                      description={
                        <Group gap={4}>
                          <Badge
                            size="xs"
                            variant="light"
                            color={RING_COLORS[
                              constellation?.rings.find((r) => r.ringId === step.ring)?.ringNumber ?? 0
                            ] ?? 'gray'}
                          >
                            {step.ring}
                          </Badge>
                          <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>
                            {step.statusField}
                          </Text>
                        </Group>
                      }
                    />
                  ))}
                </Stepper>
              </Collapse>
            </Paper>
          </>
        )}
      </Stack>

      {/* ── Fullscreen Constellation Modal ──────────────────────────── */}
      <Modal
        opened={graphExpanded}
        onClose={closeGraphModal}
        fullScreen
        title={
          <Group gap="xs">
            <Title order={4}>Constellation Explorer</Title>
            {universalData && (
              <Badge variant="filled" color="grape" size="sm">
                {universalData.center.name}
              </Badge>
            )}
            <SegmentedControl
              size="xs"
              value={layoutMode}
              onChange={(v) => setLayoutMode(v as ConstellationLayoutMode)}
              data={[
                { label: 'Rings', value: 'concentric' },
                { label: 'Mind Map', value: 'mindmap' },
              ]}
            />
          </Group>
        }
      >
        {viewMode === 'definition' && ring3DefinitionNodes.length > 0 && definitionModeData ? (
          <ConstellationView
            center={definitionModeData.center}
            nodes={definitionModeData.nodes}
            edges={definitionModeData.edges}
            height={window.innerHeight - 120}
            mode="definition"
            definitionNodes={ring3DefinitionNodes}
            onWorkflowClick={handleWorkflowClick}
            ringMeta={ringMeta}
          />
        ) : universalData ? (
          <UniversalConstellationGraph
            constellation={universalData}
            height={window.innerHeight - 120}
            layoutMode={layoutMode}
            minimal
          />
        ) : null}
      </Modal>
    </Container>
  );
}
